import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { Box, CircularProgress, Typography, useTheme, alpha } from '@mui/material';
import { Refresh as RefreshIcon, ArrowDownward as ArrowIcon } from '@mui/icons-material';

/**
 * Pull-to-Refresh Component
 * Premium touch-based refresh pattern with smooth animations and haptic-like feel
 */
const PullToRefreshComponent = ({
  children,
  onRefresh,
  threshold = 80,
  disabled = false,
  ...props
}) => {
  const theme = useTheme();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [isPulling, setIsPulling] = useState(false);
  const [scrollTop, setScrollTop] = useState(0);
  const startYRef = useRef(0);
  const containerRef = useRef(null);

  // Sync scroll position to enable/disable pull-to-refresh
  useEffect(() => {
    const handleScroll = () => {
      setScrollTop(window.scrollY);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleTouchStart = useCallback((e) => {
    // Only allow pulling if we are at the very top of the page
    if (disabled || isRefreshing || scrollTop > 0) return;
    startYRef.current = e.touches[0].clientY;
    setIsPulling(true);
  }, [disabled, isRefreshing, scrollTop]);

  const handleTouchMove = useCallback((e) => {
    if (!isPulling || disabled || isRefreshing || scrollTop > 0) return;

    const currentY = e.touches[0].clientY;
    const distance = currentY - startYRef.current;

    console.log('Touch Move distance:', distance);

    if (distance > 0) {
      // Add damping effect (resistance increases as user pulls further)
      const dampedDistance = Math.pow(distance, 0.85);
      setPullDistance(Math.min(dampedDistance, threshold + 40));

      // Prevent default to avoid browser's native pull-to-refresh
      if (distance > 10 && e.cancelable) {
        e.preventDefault();
      }
    } else {
      setPullDistance(0);
    }
  }, [isPulling, disabled, isRefreshing, threshold]);

  const handleTouchEnd = useCallback(async () => {
    if (!isPulling) return;
    setIsPulling(false);

    if (pullDistance >= threshold && !isRefreshing && onRefresh) {
      setIsRefreshing(true);
      setPullDistance(threshold); // Stay at threshold height while refreshing
      try {
        await onRefresh();
      } catch (error) {
        console.error('Refresh failed:', error);
      } finally {
        setIsRefreshing(false);
        setPullDistance(0);
      }
    } else {
      setPullDistance(0);
    }
  }, [pullDistance, threshold, isRefreshing, onRefresh, isPulling]);

  // Critical: Attach non-passive listener to allow e.preventDefault() to work in built apps
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const onTouchMove = (e) => {
      // We call the logic here
      handleTouchMove(e);
    };

    el.addEventListener('touchmove', onTouchMove, { passive: false });
    return () => el.removeEventListener('touchmove', onTouchMove);
  }, [handleTouchMove]);

  const rotation = Math.min((pullDistance / threshold) * 180, 180);
  const opacity = Math.min(pullDistance / threshold, 1);

  return (
    <Box
      ref={containerRef}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onMouseDown={(e) => {
        if (disabled || isRefreshing || window.scrollY > 0) return;
        startYRef.current = e.clientY;
        setIsPulling(true);
      }}
      onMouseMove={(e) => {
        if (!isPulling || disabled || isRefreshing || window.scrollY > 0) return;
        const distance = e.clientY - startYRef.current;
        if (distance > 0) {
          const dampedDistance = Math.pow(distance, 0.85);
          setPullDistance(Math.min(dampedDistance, threshold + 40));
        }
      }}
      onMouseUp={handleTouchEnd}
      onMouseLeave={() => {
        if (isPulling) handleTouchEnd();
      }}
      sx={{
        position: 'relative',
        width: '100%',
        minHeight: '100%',
        // Relax touch-action to allow vertical scrolling (pan-y) while still catching the pull gesture
        touchAction: disabled ? 'auto' : 'pan-y pan-x', 
        cursor: isPulling ? 'grabbing' : 'auto',
        transition: isPulling ? 'none' : 'transform 0.4s cubic-bezier(0.19, 1, 0.22, 1)',
        transform: `translateY(${pullDistance}px)`,
        ...props.sx
      }}
      {...props}
    >
      {/* Refresh Indicator Overlay */}
      <Box
        sx={{
          position: 'absolute',
          top: -60,
          left: 0,
          right: 0,
          height: 60,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: isRefreshing ? 1 : opacity,
          transition: 'opacity 0.2s',
          zIndex: 10,
          pointerEvents: 'none',
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            backgroundColor: theme.palette.background.paper,
            boxShadow: '0 4px 10px rgba(0,0,0,0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transform: `rotate(${isRefreshing ? 0 : rotation}deg)`,
            transition: isRefreshing ? 'none' : 'transform 0.1s linear',
            color: theme.palette.primary.main,
          }}
        >
          {isRefreshing ? (
            <CircularProgress size={24} color="primary" thickness={5} />
          ) : (
            <ArrowIcon
              sx={{
                fontSize: 24,
                transform: pullDistance >= threshold ? 'rotate(180deg)' : 'none',
                transition: 'transform 0.2s'
              }}
            />
          )}
        </Box>
        <Typography
          variant="caption"
          sx={{
            mt: 0.5,
            fontWeight: 600,
            color: theme.palette.text.secondary,
            textShadow: '0 1px 2px rgba(0,0,0,0.1)'
          }}
        >
          {isRefreshing ? 'Refreshing...' : pullDistance >= threshold ? 'Release to refresh' : 'Pull to refresh'}
        </Typography>
      </Box>

      {/* Main Content */}
      <Box sx={{ width: '100%', height: '100%' }}>
        {children}
      </Box>
    </Box>
  );
};

PullToRefreshComponent.propTypes = {
  children: PropTypes.node.isRequired,
  onRefresh: PropTypes.func,
  threshold: PropTypes.number,
  disabled: PropTypes.bool,
  sx: PropTypes.object
};

export default PullToRefreshComponent;
